import React, { useCallback, useEffect, useState } from 'react';
import { StateType } from 'reducer';
import { PodiatryConditionalOptionsType } from 'types/types';
import {
  ActiveTypeOfReasonForVisit,
  usePersistRFVReducer,
} from 'reducer/reasonForVisit';
import { actionTypesOfReasonForVisit } from 'reducer/actionType';
import InputText from 'components/common/InputText';
import styles from 'components/podiatry/page.module.scss';
import MetaTag from 'components/meta';
import union from 'lodash/union';
import Button from 'components/common/button';
import clsx from 'clsx';

const NoneText: string = 'None';
type Props = {
  options: PodiatryConditionalOptionsType | PodiatryConditionalOptionsType[];
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function WhatTreatmentsWereHelpful({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  isFilter = false,
}: Props) {
  const [inputText, setInputText] = useState('');
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionOptions, setQuestionOptions] = useState<string[]>([NoneText]);
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [inputTextError, setInputTextError] = useState<boolean>(false);

  const [state, dispatch] = usePersistRFVReducer();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: PodiatryConditionalOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        const treatmentsAtHome = state.find(
          (object: StateType) => object.name === actionTypesOfReasonForVisit.TREATMENTS_TRIED_HOME,
        );
        const treatMentsFromAnotherProvider = state.find(
          (object: StateType) => object.name
            === actionTypesOfReasonForVisit.WHAT_TREATMENTS_DID_PROVIDER,
        );

        if (treatmentsAtHome && treatMentsFromAnotherProvider) {
          const uniqueOptions: string[] = union(
            treatmentsAtHome.value,
            treatMentsFromAnotherProvider.value,
          );
          setQuestionOptions((prev) => [...prev, ...uniqueOptions]);
        }
        setTitle(findOptions?.title ?? '');
        setSubTitle(findOptions?.subTitle ?? '');
      }
    } else if (!Array.isArray(options)) {
      setQuestionOptions(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
  }, []);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        setSelectedOption(data.value);
        setInputText(data.text ?? '');
      }
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      if (item === NoneText) {
        setSelectedOption([item]);
      } else {
        setSelectedOption((old) => {
          if (old.toString() === NoneText) {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedOption([item]);
    }

    setError(false);
    setInputTextError(false);
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (selectedOption.length === 0) {
      setError(true);
      return;
    }

    const dispatchPayload: ActiveTypeOfReasonForVisit = {
      type: actionPage,
      name: title,
      value: selectedOption,
      text: inputText,
    };
    const other = selectedOption.find((item) => item === 'Other');
    if (other) {
      if (!inputText.trim()) {
        setInputTextError(true);
        return;
      }
    }

    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage, true);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    setInputTextError(false);
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
      <p className="error-message">
        Please select at least one option.
      </p>
      )}

      <div>
        {questionOptions.map((option) => {
          const selectedClass = selectedOption.find(
            (item) => item === option,
          );
          return (
            <div key={option}>
              <p
                className={clsx('option', { 'selected-option': selectedClass })}
                onClick={() => onSelectOptions(option)}
                onKeyUp={() => {}}
              >
                {option}
              </p>
              {option === 'Other' && selectedClass && (
              <InputText
                value={inputText}
                placeholder="Type..."
                size="small"
                error={inputTextError}
                isWidthFull
                onChange={onChangeText}
              />
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          Submit
        </Button>
      </div>
    </>
  );
}

export default WhatTreatmentsWereHelpful;
